import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  BarChart3, Building2, Camera, Car, ChevronDown, CreditCard,
  History, LayoutDashboard, LogOut, Menu, Monitor, Settings,
  Shield, Truck, Users, Wallet, X,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png';

const items = [
  { to: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
  { to: '/kiosk', label: 'Terminal', icon: Monitor, roles: ['super_admin', 'admin', 'operator', 'cashier'] },
  { to: '/detection', label: 'Escanear', icon: Camera, roles: ['super_admin', 'admin', 'operator'] },
  { to: '/history', label: 'Historial', icon: History },
  { to: '/members', label: 'Socios', icon: Users, roles: ['super_admin', 'admin'] },
  { to: '/member-payments', label: 'Cuotas', icon: CreditCard, roles: ['super_admin', 'admin', 'cashier'] },
  { to: '/vehicles', label: 'Vehículos', icon: Truck, roles: ['super_admin', 'admin'] },
  { to: '/cashier', label: 'Caja', icon: Wallet, roles: ['super_admin', 'admin', 'cashier'] },
  { to: '/sites', label: 'Sedes', icon: Building2, roles: ['super_admin', 'admin'] },
  { to: '/analytics', label: 'Analítica', icon: BarChart3, roles: ['super_admin', 'admin'] },
];

const configurationItems = [
  { to: '/settings', label: 'Configuración', icon: Settings, roles: ['super_admin', 'admin'] },
  { to: '/users', label: 'Usuarios', icon: Users, roles: ['super_admin', 'admin'] },
  { to: '/cameras', label: 'Cámaras', icon: Camera, roles: ['super_admin', 'admin'] },
  { to: '/connectors', label: 'Conectores', icon: Car, roles: ['super_admin', 'admin'] },
  { to: '/super-admin', label: 'Super Admin', icon: Shield, roles: ['super_admin'] },
];

const canSee = (item, role) => !item.roles || item.roles.includes(role);

export default function AppNavigation() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  if (location.pathname.startsWith('/kiosk')) {
    return null;
  }

  const visible = items.filter((item) => canSee(item, user?.role));
  const config = configurationItems.filter((item) => canSee(item, user?.role));
  const isActive = (to) => location.pathname === to || (to !== '/dashboard' && location.pathname.startsWith(`${to}/`));

  const navLink = (item, mobile = false) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => { setOpen(false); setConfigOpen(false); }}
        className={`${mobile ? 'flex items-center gap-3 rounded-lg px-3 py-3' : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm'} transition-colors ${
          isActive(item.to)
            ? 'bg-emerald-600 text-white'
            : 'text-slate-300 hover:bg-slate-700 hover:text-white'
        }`}
        aria-current={isActive(item.to) ? 'page' : undefined}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-700 bg-slate-900/95 backdrop-blur">
      <div className="mx-auto flex min-h-14 w-full max-w-7xl items-center gap-3 px-3 py-2 sm:px-6 lg:px-8">
        <Link to="/dashboard" className="flex min-w-0 items-center gap-2" onClick={() => setOpen(false)}>
          <img src={LOGO_URL} alt="ParkingVision AI" className="h-7 w-auto shrink-0 sm:h-8" />
          <span className="hidden whitespace-nowrap text-sm font-bold text-white sm:inline">Parking Vision</span>
        </Link>

        <nav className="ml-auto hidden min-w-0 items-center gap-0.5 xl:flex" aria-label="Navegación principal">
          {visible.map((item) => navLink(item))}
          {config.length > 0 && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfigOpen((value) => !value)}
                className="text-slate-300 hover:bg-slate-700 hover:text-white"
                aria-expanded={configOpen}
              >
                <Settings className="mr-1.5 h-4 w-4" />
                Más
                <ChevronDown className={`ml-1 h-3 w-3 transition-transform ${configOpen ? 'rotate-180' : ''}`} />
              </Button>
              {configOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 rounded-lg border border-slate-700 bg-slate-800 p-1 shadow-xl">
                  {config.map((item) => navLink(item, true))}
                </div>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => { await logout(); navigate('/login'); }}
            className="text-slate-400 hover:bg-slate-700 hover:text-white"
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </nav>

        <Button
          variant="ghost"
          size="sm"
          className="ml-auto text-slate-300 xl:hidden"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {open && (
        <nav className="max-h-[calc(100dvh-3.5rem)] overflow-y-auto border-t border-slate-700 bg-slate-900 p-3 xl:hidden" aria-label="Navegación móvil">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3">
            {visible.map((item) => navLink(item, true))}
            {config.map((item) => navLink(item, true))}
            <button
              type="button"
              onClick={async () => { setOpen(false); await logout(); navigate('/login'); }}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-red-400 hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
